'use client';

import IconMusicNote from '@/context/global/components/Icon/IconMusicNote';
import { cn } from '@/context/global/utils/cn';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

type SongAddedToastProps = {
  songTitle: string | null;
};

const SongAddedToast = ({ songTitle }: SongAddedToastProps) => {
  const t = useTranslations('SongAddedToast');
  const [isVisible, setIsVisible] = useState(false);
  const [displayTitle, setDisplayTitle] = useState<string | null>(null);

  useEffect(() => {
    if (songTitle) {
      setDisplayTitle(songTitle);
      setIsVisible(true);
    } else {
      setIsVisible(false);
      // Keep displayTitle until animation ends
      const timeout = setTimeout(() => setDisplayTitle(null), 300);
      return () => clearTimeout(timeout);
    }
  }, [songTitle]);

  if (!displayTitle) return null;

  return (
    <>
      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideOutDown {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(20px);
          }
        }
        .toast-enter {
          animation: slideInUp 0.3s ease-out forwards;
        }
        .toast-exit {
          animation: slideOutDown 0.3s ease-in forwards;
        }
      `}</style>
      <div
        className={cn(
          'fixed bottom-4 left-4 z-50 flex max-w-[90vw] items-center gap-2 rounded-full px-3 py-1.5 sm:bottom-8 sm:left-8 sm:px-4 sm:py-2.5',
          'bg-black/60 backdrop-blur-[5px]',
          isVisible ? 'toast-enter' : 'toast-exit',
        )}
      >
        <IconMusicNote width={20} height={20} className="shrink-0 sm:h-8 sm:w-8" />
        <p className="truncate text-base text-white sm:text-xl md:text-[28px] md:leading-[34px] lg:text-[38px] lg:leading-[44px]">
          {t('added', { title: displayTitle })}
        </p>
      </div>
    </>
  );
};

export default SongAddedToast;
